/**
 * Match the reading levels — the whole-book loudness pass, as a dialog.
 *
 * The problem it presents is one a creator can hear but not see: page 4 was
 * recorded in a quiet room and page 5 in a kitchen. So the dialog leads with the
 * *spread* — how far apart the book's pages are, in LU — and with where they will
 * land, rather than with a list of decibels. The list is there to be checked, not
 * to be read.
 *
 * It is deliberately not a one-tap button. A pass that rewrites nineteen pages of
 * somebody's recordings should show what it is about to do first, and — because
 * the rewrite is a real edit — offer the way back afterwards.
 *
 * Presentational: the phases and the plan arrive as props from
 * `useFlashBookLevelMatch`, so every state here can be rendered without a book.
 */
import {type ReactNode} from 'react'
import {type StyleProp, type TextStyle, View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {
  formatGain,
  formatLufs,
  formatSpread,
  type LevelMatchPlan,
} from '#/lib/flashbookLoudness'
import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {
  type LevelMatchPhase,
  type LevelMatchState,
} from '#/components/flashbooks/useFlashBookLevelMatch'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

/** How many rows before the list summarises itself. */
const MAX_ROWS = 8

/**
 * The spread at which a book counts as level, in LU.
 *
 * A shade wider than the planner's own tolerance: the planner decides what to
 * *change*, and this decides what to *claim*. A whole decibel across a book is
 * inaudible page to page, and a claim held to a tighter bound than that would
 * start failing on books the creator would call perfectly even.
 */
const CONSISTENT_LU = 1

/**
 * Numbers that line up down a column.
 *
 * Not an atom in this codebase, and worth the four characters: a column of
 * `−20.0` and `−8.4` in a proportional face reads as ragged, and this dialog is
 * nothing but columns of numbers.
 */
const TABULAR: StyleProp<TextStyle> = {fontVariant: ['tabular-nums']}

const BUSY: LevelMatchPhase[] = ['measuring', 'applying']

export function FlashBookLevelMatchDialog({
  control,
  state,
  onApply,
  onUndo,
  onClose,
}: {
  control: Dialog.DialogControlProps
  state: LevelMatchState
  onApply: () => void
  onUndo: () => void
  onClose: () => void
}) {
  return (
    <Dialog.Outer
      control={control}
      nativeOptions={{preventExpansion: true}}
      webOptions={{alignCenter: true}}>
      <Dialog.Handle />
      <DialogInner
        state={state}
        onApply={onApply}
        onUndo={onUndo}
        onClose={onClose}
      />
    </Dialog.Outer>
  )
}

function DialogInner({
  state,
  onApply,
  onUndo,
  onClose,
}: {
  state: LevelMatchState
  onApply: () => void
  onUndo: () => void
  onClose: () => void
}) {
  const {t: l} = useLingui()
  return (
    <Dialog.ScrollableInner
      style={web({maxWidth: 460})}
      label={l`Match the reading levels`}>
      {/*
        The anchor a test reads. On a real `View` and not on the dialog shell,
        because the shell's `testID` is a native-only prop and never reaches the
        DOM — which is how a suite ends up asserting against nothing.
      */}
      <View testID="flashbookLevelDialog" style={[a.gap_2xl]}>
        <View style={[a.gap_sm]}>
          <Text style={[a.font_bold, a.text_2xl]}>
            <Trans>Match the reading levels</Trans>
          </Text>
          <Body state={state} />
        </View>
        <Actions
          state={state}
          onApply={onApply}
          onUndo={onUndo}
          onClose={onClose}
        />
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}

/** Everything under the title — one reading of the state, and always true. */
function Body({state}: {state: LevelMatchState}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const muted = [t.atoms.text_contrast_medium, a.text_md, a.leading_snug]

  if (state.phase === 'idle' || state.phase === 'measuring') {
    return (
      <View style={[a.gap_md]}>
        <Text style={muted}>
          <Trans>
            Reading every page so they can be brought to the same level.
          </Trans>
        </Text>
        <Progress
          done={state.done}
          total={state.total}
          label={l`Measuring page ${state.done} of ${state.total}`}
        />
      </View>
    )
  }

  if (state.phase === 'failed') {
    // The one failure that is about the device rather than about a clip, so it
    // is stated plainly instead of dressed up as something to retry.
    return (
      <Text style={muted}>
        <Trans>
          This device has no audio engine, so levels cannot be matched here.
        </Trans>
      </Text>
    )
  }

  if (state.phase === 'applying') {
    return (
      <View style={[a.gap_md]}>
        <Text style={muted}>
          <Trans>Rewriting the pages that need it.</Trans>
        </Text>
        <Progress
          done={state.done}
          total={state.total}
          label={l`Rewriting page ${state.done} of ${state.total}`}
        />
      </View>
    )
  }

  const plan = state.plan
  if (!plan) {
    return (
      <Text style={muted}>
        <Trans>This book has no reading audio to match yet.</Trans>
      </Text>
    )
  }

  if (state.clipCount === 0) {
    return (
      <Text style={muted}>
        <Trans>
          Add a recording to a page first — there is nothing to measure yet.
        </Trans>
      </Text>
    )
  }

  if (state.clipCount === 1) {
    // One reading is not an uneven book, and "already level" would imply the
    // question had been asked and answered when it has not been asked.
    return (
      <Text style={muted}>
        <Trans>
          This book has a single reading, so there is nothing to match it
          against yet. Add another page and this will bring them together.
        </Trans>
      </Text>
    )
  }

  if (state.phase === 'done') return <DoneBody state={state} />
  return <ReviewBody state={state} plan={plan} />
}

function ReviewBody({
  state,
  plan,
}: {
  state: LevelMatchState
  plan: LevelMatchPlan
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  /*
   * Named once, here, where `l` is in scope.
   *
   * The cover is not a page number, and a page does not name a reading: a rich
   * page holds three or four, so the kind is what tells them apart.
   */
  /*
   * `l` is written as a tagged template *here*, not handed to a helper.
   *
   * `@lingui/react/macro` rewrites those templates at build time, and it can
   * only do that where it can see the binding that came from `useLingui()` — a
   * helper taking `l` as a parameter gets the untransformed runtime form, which
   * renders nothing at all. The first version of this did exactly that and every
   * row read "Page 3 · ".
   */
  const kindName = (kind: string) =>
    kind === 'quote'
      ? l`Quote`
      : kind === 'callout'
        ? l`Callout`
        : kind === 'caption'
          ? l`Caption`
          : kind === 'table'
            ? l`Table`
            : kind === 'formula'
              ? l`Formula`
              : l`Reading`
  const labelOf = new Map(
    state.clips.map(clip => [
      clip.id,
      clip.page === null
        ? l`Cover`
        : `${l`Page ${clip.page + 1}`} · ${kindName(clip.kind)}`,
    ]),
  )
  const rows = plan.changes.slice(0, MAX_ROWS)
  const hidden = plan.changes.length - rows.length

  if (plan.targetLufs === null) {
    return (
      <Text style={[t.atoms.text_contrast_medium, a.text_md, a.leading_snug]}>
        <Trans>
          None of these readings could be measured, so there is nothing to
          match.
        </Trans>
      </Text>
    )
  }

  if (plan.isEmpty) {
    return (
      <View style={[a.gap_lg]}>
        <Text style={[t.atoms.text_contrast_medium, a.text_md, a.leading_snug]}>
          <Trans>
            Every reading is already at the same level, so nothing will be
            rewritten.
          </Trans>
        </Text>
        {plan.spreadBefore && (
          <SpreadNote before={plan.spreadBefore.spread} after={null} />
        )}
        <Caveats state={state} plan={plan} />
      </View>
    )
  }

  return (
    <View style={[a.gap_lg]}>
      <Text style={[t.atoms.text_contrast_medium, a.text_md, a.leading_snug]}>
        <Trans>
          Bringing {plan.changes.length}{' '}
          {plan.changes.length === 1 ? 'recording' : 'recordings'} to{' '}
          {formatLufs(plan.targetLufs)} — the middle of what this book already
          sounds like.
        </Trans>
      </Text>

      {plan.spreadBefore && (
        <SpreadNote
          before={plan.spreadBefore.spread}
          after={plan.spreadAfter ? plan.spreadAfter.spread : null}
        />
      )}

      <View style={[a.gap_xs]}>
        {rows.map(change => (
          <View key={change.id} style={[a.flex_row, a.align_center, a.gap_sm]}>
            <Text
              style={[t.atoms.text_contrast_high, a.text_md, a.flex_1]}
              numberOfLines={1}>
              {labelOf.get(change.id) ?? change.id}
            </Text>
            <Text style={[t.atoms.text_contrast_medium, a.text_md, TABULAR]}>
              {formatLufs(change.lufs)}
            </Text>
            <Text
              style={[
                t.atoms.text_contrast_high,
                a.text_md,
                a.font_bold,
                TABULAR,
              ]}>
              {formatGain(change.gainDb)}
            </Text>
          </View>
        ))}
        {hidden > 0 && (
          <Text style={[t.atoms.text_contrast_low, a.text_sm]}>
            <Trans>and {hidden} more</Trans>
          </Text>
        )}
      </View>

      <Caveats state={state} plan={plan} />
    </View>
  )
}

function DoneBody({state}: {state: LevelMatchState}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const plan = state.plan
  /*
   * The measured spread, not the plan's prediction of it.
   *
   * This is the number a creator will believe and repeat, so it is the one the
   * pass went back and measured after rewriting — and when that verification
   * could not run, the sentence falls back to saying only what was rewritten
   * rather than quoting a figure nobody checked.
   */
  const after = state.verifiedSpread
  const consistent = after !== null && after <= CONSISTENT_LU

  return (
    <View style={[a.gap_lg]}>
      <Text style={[t.atoms.text_contrast_medium, a.text_md, a.leading_snug]}>
        {state.applied === 1
          ? l`One recording was rewritten.`
          : l`${state.applied} recordings were rewritten.`}
        {/*
          The claim, and only when the measurement is behind it.

          "Consistent" is appended from the *verified* spread rather than from
          the fact that a render happened: a book whose pages still disagree by
          three LU has been rewritten and is not consistent, and it is this
          sentence a creator would act on.
        */}
        {consistent ? ' ' + l`The book now reads at a consistent level.` : ''}
      </Text>
      {plan?.spreadBefore && after !== null && (
        <SpreadNote before={plan.spreadBefore.spread} after={after} />
      )}
      {after === null && (
        <NoteText>
          <Trans>
            The result could not be measured back, so listen to a page before
            you publish.
          </Trans>
        </NoteText>
      )}
      {plan && <Caveats state={state} plan={plan} />}
    </View>
  )
}

/**
 * The three things that stop a pass being a clean sweep, said out loud.
 *
 * A dialog that only reported success would be wrong about a book with an
 * unreadable page in it, and the creator is the only one who can do anything
 * about any of them.
 */
function Caveats({
  state,
  plan,
}: {
  state: LevelMatchState
  plan: LevelMatchPlan
}) {
  const {t: l} = useLingui()
  /*
   * Read from the plan in *every* phase, including "done".
   *
   * The first version only reported this before applying, so the success screen
   * said "the book now reads at a consistent level" about a book with a page the
   * ceiling had stopped short — the one claim that screen must never get wrong,
   * because it is the screen a creator trusts and moves on from.
   */
  const heldBack = plan.changes.filter(
    change => change.limitedBy !== 'target',
  ).length
  const silent = plan.silent.length
  const unreadable = state.failures.length

  if (heldBack === 0 && silent === 0 && unreadable === 0) return null

  return (
    <View style={[a.gap_xs]}>
      {heldBack > 0 && (
        <NoteText>
          {heldBack === 1
            ? l`One recording cannot reach the level without clipping, so it stays quieter than the rest.`
            : l`${heldBack} recordings cannot reach the level without clipping, so they stay quieter than the rest.`}
        </NoteText>
      )}
      {silent > 0 && (
        <NoteText>
          {silent === 1
            ? l`One reading is silent and is left exactly as it is.`
            : l`${silent} readings are silent and are left exactly as they are.`}
        </NoteText>
      )}
      {unreadable > 0 && (
        <NoteText>
          {state.phase === 'done'
            ? unreadable === 1
              ? l`One reading could not be rewritten and is unchanged.`
              : l`${unreadable} readings could not be rewritten and are unchanged.`
            : unreadable === 1
              ? l`One reading could not be opened, so it is not part of this match.`
              : l`${unreadable} readings could not be opened, so they are not part of this match.`}
        </NoteText>
      )}
    </View>
  )
}

/**
 * The one number the creator came for.
 *
 * `6.4 LU apart → 0.2 LU apart`. A before-and-after rather than an after alone,
 * because the reason they opened this dialog is the number they can already hear.
 */
function SpreadNote({before, after}: {before: number; after: number | null}) {
  const t = useTheme()
  const {t: l} = useLingui()
  return (
    <View
      style={[
        a.flex_row,
        a.align_center,
        a.flex_wrap,
        a.gap_sm,
        a.px_md,
        a.py_sm,
        a.rounded_md,
        t.atoms.bg_contrast_25,
      ]}>
      <Text
        style={[t.atoms.text_contrast_medium, a.text_md]}>{l`Pages are`}</Text>
      <Text
        style={[t.atoms.text_contrast_high, a.text_md, a.font_bold, TABULAR]}>
        {formatSpread(before)}
      </Text>
      <Text style={[t.atoms.text_contrast_medium, a.text_md]}>{l`apart`}</Text>
      {after !== null && (
        <>
          <Text style={[t.atoms.text_contrast_low, a.text_md]}>→</Text>
          <Text
            style={[
              t.atoms.text_contrast_high,
              a.text_md,
              a.font_bold,
              TABULAR,
            ]}>
            {formatSpread(after)}
          </Text>
        </>
      )}
    </View>
  )
}

/** A caveat the creator may need to act on. */
function NoteText({children}: {children: ReactNode}) {
  const t = useTheme()
  return (
    <Text style={[t.atoms.text_contrast_low, a.text_sm, a.leading_snug]}>
      {children}
    </Text>
  )
}

/**
 * The progress indicator.
 *
 * Drawn rather than imported: the app has no shared progress bar, and a bar is
 * two views. It earns its place because the pass reads a whole book — on a large
 * one that is a second or more per page, and a bare spinner reads as a hang.
 */
function Progress({
  done,
  total,
  label,
}: {
  done: number
  total: number
  label: string
}) {
  const t = useTheme()
  const fraction = total > 0 ? Math.min(1, Math.max(0, done / total)) : 0
  return (
    <View style={[a.gap_xs]}>
      <View
        style={[
          a.w_full,
          a.rounded_full,
          a.overflow_hidden,
          t.atoms.bg_contrast_25,
          {height: 4},
        ]}>
        <View
          style={[
            a.rounded_full,
            t.atoms.bg_contrast_700,
            {height: 4, width: `${fraction * 100}%`},
          ]}
        />
      </View>
      <Text
        accessibilityLiveRegion="polite"
        style={[t.atoms.text_contrast_low, a.text_sm, TABULAR]}>
        {label}
      </Text>
    </View>
  )
}

function Actions({
  state,
  onApply,
  onUndo,
  onClose,
}: {
  state: LevelMatchState
  onApply: () => void
  onUndo: () => void
  onClose: () => void
}) {
  const {t: l} = useLingui()

  if (BUSY.includes(state.phase)) {
    return (
      <View style={[a.flex_row, a.align_center, a.justify_center, a.py_xs]}>
        <Loader size="lg" />
      </View>
    )
  }

  if (state.phase === 'done') {
    return (
      <View style={[a.gap_sm]}>
        <Button
          label={l`Undo the level match`}
          accessibilityHint={l`Puts every page back to the recording it had`}
          onPress={onUndo}
          size="large"
          variant="solid"
          color="secondary"
          testID="flashbookLevelUndoBtn">
          <ButtonText>
            <Trans>Undo</Trans>
          </ButtonText>
        </Button>
        <Button
          label={l`Done`}
          onPress={onClose}
          size="large"
          variant="ghost"
          color="secondary"
          testID="flashbookLevelDoneBtn">
          <ButtonText>
            <Trans>Done</Trans>
          </ButtonText>
        </Button>
      </View>
    )
  }

  if (state.phase === 'review' && state.plan && !state.plan.isEmpty) {
    return (
      <View style={[a.gap_sm]}>
        <Button
          label={l`Match the levels`}
          accessibilityHint={l`Rewrites the pages that are out of step`}
          onPress={onApply}
          size="large"
          variant="solid"
          color="primary"
          testID="flashbookLevelApplyBtn">
          <ButtonText>
            <Trans>Match levels</Trans>
          </ButtonText>
        </Button>
        <Button
          label={l`Cancel`}
          accessibilityHint={l`Leaves every page as it is`}
          onPress={onClose}
          size="large"
          variant="ghost"
          color="secondary"
          testID="flashbookLevelCancelBtn">
          <ButtonText>
            <Trans>Cancel</Trans>
          </ButtonText>
        </Button>
      </View>
    )
  }

  // Nothing to do, or nothing measurable: the only way on is back.
  return (
    <Button
      label={l`Close`}
      onPress={onClose}
      size="large"
      variant="ghost"
      color="secondary"
      testID="flashbookLevelCloseBtn">
      <ButtonText>
        <Trans>Close</Trans>
      </ButtonText>
    </Button>
  )
}
